import { useState } from 'react';

// Structural subset of useSpaces' SpaceSearchResult — only what the panel
// renders. Defined locally to avoid back-importing from the hook (no circular
// imports; mirrors SpaceRail/DmSidebar).
type SpaceSearchResult = {
  id: string;
  name?: string;
  avatarUrl?: string | null;
  description?: string | null;
  requestStatus?: string | null;
};

type SpaceDiscoveryPanelProps = {
  searchSpaces: (query: string) => Promise<SpaceSearchResult[]>;
  requestJoinSpace: (spaceId: string) => Promise<{ status: string }>;
};

// Landing-page "find a space" widget: search public spaces by name and request
// to join (an owner approves). Owns all of its own search/request state.
function SpaceDiscoveryPanel({ searchSpaces, requestJoinSpace }: SpaceDiscoveryPanelProps) {
  const [landingSearchQuery, setLandingSearchQuery] = useState('');
  const [landingSearchResults, setLandingSearchResults] = useState<SpaceSearchResult[]>([]);
  const [landingSearchBusy, setLandingSearchBusy] = useState(false);
  const [landingSearchErr, setLandingSearchErr] = useState('');
  const [landingSearchTouched, setLandingSearchTouched] = useState(false);
  const [landingRequestingId, setLandingRequestingId] = useState<string | null>(null);

  async function runLandingSearch() {
    const q = landingSearchQuery.trim();
    setLandingSearchTouched(true);
    if (q.length < 2) {
      setLandingSearchResults([]);
      setLandingSearchErr('Type at least 2 characters');
      return;
    }
    setLandingSearchBusy(true);
    setLandingSearchErr('');
    try {
      const res = await searchSpaces(q);
      setLandingSearchResults(res);
    } catch (e: any) {
      setLandingSearchErr(e?.message || 'Search failed');
    } finally {
      setLandingSearchBusy(false);
    }
  }

  async function requestLandingJoin(spaceId: string) {
    setLandingRequestingId(spaceId);
    setLandingSearchErr('');
    try {
      const res = await requestJoinSpace(spaceId);
      const status = (res as any)?.status || 'pending';
      setLandingSearchResults((prev) => prev.map((r) => r.id === spaceId ? { ...r, requestStatus: status } : r));
    } catch (e: any) {
      setLandingSearchErr(e?.message || 'Request failed');
    } finally {
      setLandingRequestingId(null);
    }
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4  space-y-3">
      <div className="text-sm font-medium text-white">Find a space</div>
      <p className="text-xs text-fg-2">Search by name and request to join. An owner must approve your request.</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <form className="w-full" onSubmit={(e) => { e.preventDefault(); runLandingSearch(); }}>
          <input
            className="w-full h-10 px-3 rounded-md bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none accent-ring focus:ring-2"
            placeholder="Search spaces"
            aria-label="Search spaces"
            enterKeyHint="search"
            value={landingSearchQuery}
            onChange={(e) => setLandingSearchQuery(e.target.value)}
            disabled={landingSearchBusy}
          />
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
        <button
          className="px-3 py-2 rounded border border-neutral-700 text-fg hover:bg-neutral-800/70 disabled:opacity-60"
          onClick={runLandingSearch}
          disabled={landingSearchBusy || landingSearchQuery.trim().length < 2}
        >
          {landingSearchBusy ? 'Searching...' : 'Search'}
        </button>
      </div>
      {landingSearchErr && <div className="text-sm text-red-300">{landingSearchErr}</div>}
      {landingSearchTouched && !landingSearchBusy && landingSearchResults.length === 0 && !landingSearchErr && (
        <div className="text-xs text-fg-3">No spaces found.</div>
      )}
      {landingSearchResults.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-auto pr-1">
          {landingSearchResults.map((s) => {
            const status = String(s.requestStatus || '').toLowerCase();
            const isPending = status === 'pending';
            const isRejected = status === 'rejected';
            return (
              <div key={s.id} className="flex items-start gap-3 rounded-lg border border-neutral-800 bg-neutral-950/60 p-3">
                <div className="h-9 w-9 rounded-full overflow-hidden border border-neutral-700 bg-neutral-800 flex items-center justify-center">
                  {s.avatarUrl ? (
                    <img src={s.avatarUrl} alt={s.name || 'space'} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xs text-fg-3">{(s.name || '?').slice(0,1).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-fg font-medium truncate">{s.name || 'Untitled space'}</div>
                  {s.description && <div className="text-xs text-fg-2 break-words">{s.description}</div>}
                  {isPending && <div className="mt-1 text-meta text-amber-300">Pending</div>}
                  {isRejected && <div className="mt-1 text-meta text-red-300">Not approved</div>}
                </div>
                <button
                  className="px-2.5 py-1.5 rounded border border-emerald-700 bg-emerald-800/70 text-emerald-50 hover:bg-emerald-700/70 disabled:opacity-60"
                  disabled={landingRequestingId === s.id || isPending}
                  onClick={() => requestLandingJoin(s.id)}
                >
                  {isRejected ? 'Request again' : 'Request'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SpaceDiscoveryPanel;
